// Global quote cart UI: a floating button (FAB) with a live count badge and a
// slide-over drawer. Loaded on every page from the frontend layout.
// Add-to-quote triggers anywhere on the site use [data-add-to-quote] with
// data-product-id / data-product-name / data-product-thumb / data-product-slug.

import { quoteStore, thumbSrc, QuoteItem, langPrefix, productHref, escapeHtml, PLACEHOLDER } from "./quoteStore";
import { safeLineId, safeQty } from "../_Shared/cartLine";

// Localized UI strings injected server-side as window.feText (English fallbacks below).
const FT: Record<string, string> = (window as any).feText || {};
const t = (key: string, fallback: string): string => FT[key] || fallback;
/** The same text HTML-escaped, for markup templates: translations are editable in Administration. */
const th = (key: string, fallback: string): string => escapeHtml(t(key, fallback));
// Keep in-drawer links in the current language (/ar, /fr).
const LP: string = langPrefix();

const ICON_BAG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
const ICON_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
const ICON_TRASH =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>';

let drawerEl: HTMLElement;
let fabEl: HTMLButtonElement;
let badgeEl: HTMLElement;
let listEl: HTMLElement;
let footerCountEl: HTMLElement;

document.addEventListener("DOMContentLoaded", init);

function init(): void {
    buildFab();
    buildDrawer();
    wireAddButtons();
    quoteStore.subscribe(render);
    render();
}

/* ───────────────────────── FAB ───────────────────────── */

function buildFab(): void {
    fabEl = document.createElement("button");
    fabEl.className = "qcart-fab";
    fabEl.type = "button";
    fabEl.setAttribute("aria-label", t("Cart.Open", "Open your quote request"));
    fabEl.innerHTML = `${ICON_BAG}<span class="qcart-fab__badge" aria-hidden="true">0</span>`;
    fabEl.addEventListener("click", openDrawer);
    badgeEl = fabEl.querySelector(".qcart-fab__badge") as HTMLElement;
    document.body.appendChild(fabEl);
}

/* ──────────────────────── Drawer ─────────────────────── */

function buildDrawer(): void {
    drawerEl = document.createElement("div");
    drawerEl.className = "qcart-drawer";
    drawerEl.setAttribute("role", "dialog");
    drawerEl.setAttribute("aria-modal", "true");
    drawerEl.setAttribute("aria-label", t("Cart.Panel", "Your quote request"));
    drawerEl.hidden = true;
    drawerEl.innerHTML = `
        <div class="qcart-drawer__scrim" data-qcart-close></div>
        <aside class="qcart-drawer__panel">
            <header class="qcart-drawer__head">
                <h2 class="qcart-drawer__title">${th("Cart.Title", "Your Quote")} <span class="qcart-drawer__count"></span></h2>
                <button class="qcart-drawer__close" type="button" aria-label="${th("Cart.Close", "Close")}" data-qcart-close>${ICON_CLOSE}</button>
            </header>
            <div class="qcart-drawer__list" data-qcart-list></div>
            <footer class="qcart-drawer__foot">
                <div class="qcart-drawer__sum"><span data-qcart-foot-count></span></div>
                <a href="${LP}/quote" class="qcart-btn qcart-btn--primary">${th("Cart.Proceed", "Proceed to Quote")}
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
                </a>
                <button class="qcart-drawer__clear" type="button" data-qcart-clear>${th("Cart.Clear", "Clear all")}</button>
            </footer>
        </aside>`;
    document.body.appendChild(drawerEl);

    listEl = drawerEl.querySelector("[data-qcart-list]") as HTMLElement;
    footerCountEl = drawerEl.querySelector("[data-qcart-foot-count]") as HTMLElement;

    drawerEl.addEventListener("click", (e) => {
        const t = e.target as HTMLElement;
        if (t.closest("[data-qcart-close]")) closeDrawer();
        if (t.closest("[data-qcart-clear]")) {
            if (quoteStore.lineCount() > 0) quoteStore.clear();
        }
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !drawerEl.hidden) closeDrawer();
    });
}

function openDrawer(): void {
    render();
    drawerEl.hidden = false;
    requestAnimationFrame(() => drawerEl.classList.add("is-open"));
    document.body.classList.add("qcart-lock");
}

function closeDrawer(): void {
    drawerEl.classList.remove("is-open");
    document.body.classList.remove("qcart-lock");
    window.setTimeout(() => { drawerEl.hidden = true; }, 320);
}

/* ──────────────────────── Render ─────────────────────── */

function render(): void {
    const items = quoteStore.getItems();
    const totalQty = quoteStore.count();

    // Badge
    badgeEl.textContent = String(totalQty);
    fabEl.classList.toggle("is-empty", totalQty === 0);

    // Drawer count chip + footer
    const countChip = drawerEl?.querySelector(".qcart-drawer__count");
    if (countChip) countChip.textContent = totalQty ? `(${totalQty})` : "";
    if (footerCountEl) footerCountEl.textContent = (totalQty === 1 ? t("Cart.Item", "{n} item") : t("Cart.Items", "{n} items")).replace("{n}", String(totalQty));

    // Reflect "in cart" state on any add buttons currently on the page
    syncAddButtons();

    if (!listEl) return;

    if (items.length === 0) {
        listEl.innerHTML = `
            <div class="qcart-empty">
                <div class="qcart-empty__ico">${ICON_BAG}</div>
                <p class="qcart-empty__t">${th("Cart.EmptyTitle", "Your quote is empty")}</p>
                <p class="qcart-empty__s">${th("Cart.EmptyText", "Browse products and tap Add to Quote to build a request.")}</p>
                <a href="${LP}/products" class="qcart-btn qcart-btn--ghost">${th("Cart.Browse", "Browse products")}</a>
            </div>`;
        const footEl = drawerEl.querySelector(".qcart-drawer__foot") as HTMLElement;
        if (footEl) footEl.classList.add("is-hidden");
        return;
    }

    const footEl = drawerEl.querySelector(".qcart-drawer__foot") as HTMLElement;
    if (footEl) footEl.classList.remove("is-hidden");

    listEl.innerHTML = items.map(rowHtml).join("");
    listEl.querySelectorAll<HTMLElement>("[data-qcart-row]").forEach(wireRow);
}

function rowHtml(item: QuoteItem): string {
    // id and qty come from localStorage: coerce them before they reach innerHTML (cartLine.ts).
    const href = escapeHtml(productHref(item, LP));
    return `
        <div class="qcart-row" data-qcart-row="${safeLineId(item.id)}">
            <a class="qcart-row__img" href="${href}">
                <img src="${escapeHtml(thumbSrc(item.thumb))}" alt="${escapeHtml(item.name)}" loading="lazy"
                     onerror="this.onerror=null;this.src='${PLACEHOLDER}';">
            </a>
            <div class="qcart-row__main">
                <a class="qcart-row__name" href="${href}">${escapeHtml(item.name)}</a>
                <div class="qcart-row__controls">
                    <div class="qcart-stepper" role="group" aria-label="${th("Cart.Qty", "Quantity")}">
                        <button type="button" class="qcart-stepper__btn" data-qcart-dec aria-label="${th("Cart.Dec", "Decrease quantity")}">−</button>
                        <input class="qcart-stepper__val" type="text" inputmode="numeric" value="${safeQty(item.qty)}" aria-label="${th("Cart.Qty", "Quantity")}" />
                        <button type="button" class="qcart-stepper__btn" data-qcart-inc aria-label="${th("Cart.Inc", "Increase quantity")}">+</button>
                    </div>
                    <button type="button" class="qcart-row__rm" data-qcart-rm aria-label="${th("Cart.Remove", "Remove")} ${escapeHtml(item.name)}">${ICON_TRASH}</button>
                </div>
            </div>
        </div>`;
}

function wireRow(row: HTMLElement): void {
    const id = Number(row.getAttribute("data-qcart-row"));
    const input = row.querySelector(".qcart-stepper__val") as HTMLInputElement;
    row.querySelector("[data-qcart-dec]")?.addEventListener("click", () =>
        quoteStore.setQty(id, currentQty(input) - 1));
    row.querySelector("[data-qcart-inc]")?.addEventListener("click", () =>
        quoteStore.setQty(id, currentQty(input) + 1));
    row.querySelector("[data-qcart-rm]")?.addEventListener("click", () => {
        row.classList.add("is-leaving");
        window.setTimeout(() => quoteStore.remove(id), 180);
    });
    input.addEventListener("change", () => quoteStore.setQty(id, currentQty(input)));
}

function currentQty(input: HTMLInputElement): number {
    const n = parseInt(input.value, 10);
    return isNaN(n) ? 1 : n;
}

/* ─────────────────── Add-to-quote triggers ────────────────── */

function wireAddButtons(): void {
    // Event delegation so dynamically-loaded product cards work too.
    document.addEventListener("click", (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-add-to-quote]");
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();

        const id = Number(btn.getAttribute("data-product-id"));
        if (!id) return;
        const name = btn.getAttribute("data-product-name") || t("Cart.Product", "Product");
        const thumb = btn.getAttribute("data-product-thumb") || "";
        const slug = btn.getAttribute("data-product-slug") || "";
        const qtyAttr = btn.getAttribute("data-product-qty");
        const qty = qtyAttr ? Math.max(1, parseInt(qtyAttr, 10) || 1) : 1;

        quoteStore.add({ id, name, thumb, slug }, qty);
        pulseFab();
        toast(t("Cart.Added", "Added to quote"));
    });
}

function syncAddButtons(): void {
    document.querySelectorAll<HTMLElement>("[data-add-to-quote]").forEach((btn) => {
        const id = Number(btn.getAttribute("data-product-id"));
        btn.classList.toggle("is-added", id > 0 && quoteStore.has(id));
    });
}

/* ─────────────────────── Feedback ────────────────────── */

function pulseFab(): void {
    fabEl.classList.remove("is-pulse");
    void fabEl.offsetWidth; // reflow to restart animation
    fabEl.classList.add("is-pulse");
}

let toastTimer: number | undefined;
function toast(msg: string): void {
    let el = document.querySelector(".qcart-toast") as HTMLElement | null;
    if (!el) {
        el = document.createElement("div");
        el.className = "qcart-toast";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        document.body.appendChild(el);
    }
    el.innerHTML = `${ICON_BAG}<span>${escapeHtml(msg)}</span>
        <button type="button" class="qcart-toast__link" data-qcart-open>${th("Cart.View", "View")}</button>`;
    el.querySelector("[data-qcart-open]")?.addEventListener("click", openDrawer);
    el.classList.add("is-show");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => el!.classList.remove("is-show"), 3200);
}

